import React, { useEffect, useState } from 'react'
import styles from '../../styles.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { integrationActions } from '../../actions'
import { getCurrentOrgId } from 'srcReact/helpers/organizationHelper'
import IntegrationButton from '../IntegrationButton'
import ConfirmModal from 'srcReact/components/common2/ConfirmModal'
import NotFound from 'srcReact/components/common2/NotFound'
import ErrorMessage from 'srcReact/components/common2/ErrorMessage'
import Spinner from 'srcReact/components/common2/Spinner'
import IconButton from 'srcReact/components/common2/IconButton'
import TooltipWrapper from 'srcReact/components/common2/TooltipWrapper'
import { isMobileScreen } from 'srcReact/helpers/utils'
import GDPRCookieConsent from 'srcReact/GDPRCookieConsent'
import DesktopHeader from 'srcReact/OrganizationSettingsPage/components/DesktopHeader'

function UserIntegrations() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isMobileDevice = isMobileScreen()
  const [orgId, setOrgId] = useState(0)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [integrationToDelete, setIntegrationToDelete] = useState()
  const [isGDPRModalOpen, setIsGDPRModalOpen] = useState(false)

  const {
    integration: { availableIntegrations, activeIntegrations, deleteIntegration }
  } = useSelector((state) => state)

  useEffect(() => {
    const organizationId = getCurrentOrgId()
    setOrgId(organizationId)
    dispatch(integrationActions.getAvailableOrgConnections(organizationId))
    getAllActiveIntegrations(organizationId, true)
  }, [])

  useEffect(() => {
    if (isConfirmModalOpen && !deleteIntegration.loading && deleteIntegration.success) {
      closeConfirmModal()
    }
  }, [deleteIntegration])

  const openGDPRModal = () => {
    setIsGDPRModalOpen(true)
  }
  const closeGDPRModal = () => {
    setIsGDPRModalOpen(false)
  }

  const closeConfirmModal = () => {
    setIsConfirmModalOpen(false)
  }

  const openConfirmModal = (integration) => {
    const { usage, external_api } = integration
    let name = ''
    switch (external_api) {
      case 'google':
      case 'gcal': {
        if (usage === 'virtual_meeting') name = 'Google meet'
        else if (usage === 'add_to_calendar') name = 'Google calendar'
        break
      }
      case 'microsoft':
      case 'microsoftcal': {
        if (usage === 'virtual_meeting') name = 'Microsoft teams'
        else if (usage === 'add_to_calendar') name = 'Microsoft outlook'
        break
      }
      case 'zoom': {
        if (usage === 'zoom') name = 'Zoom'
        break
      }
      default:
        ''
    }
    integration.name = name
    setIsConfirmModalOpen(true)
    setIntegrationToDelete(integration)
  }

  const revokeIntegration = () => {
    dispatch(
      integrationActions.revokeToken(
        integrationToDelete.type,
        integrationToDelete.usage,
        orgId,
        integrationToDelete.token,
        integrationToDelete.isDefault,
        makeDefaultIfApplicable
      )
    )
  }

  const makeDefaultIfApplicable = (type, usage, authTokenToExclude) => {
    let filtered = activeIntegrations.list.filter(
      (x) => x.usage === usage && x.id_external_api_auth !== authTokenToExclude
    )
    if (filtered.length > 0) {
      if (usage === 'virtual_meeting') {
        filtered = filtered.filter((x) => x.type === type)
        if (filtered.length > 0) {
          dispatch(integrationActions.setDefaultVirtualMeeting(type, orgId, filtered[0].id_external_api_auth))
        }
      } else if (usage === 'add_to_calendar') {
        dispatch(integrationActions.setDefaultAddToCalendar(type, orgId, filtered[0].id_external_api_auth))
      }
    }
  }

  const isVirtualMeetDefaultExists = (type) => {
    return (
      activeIntegrations.list.find((x) => x.type === type && x.usage === 'virtual_meeting' && x.is_default === true) !==
      null
    )
  }

  const getAllActiveIntegrations = (organizationId, isRefresh = false) => {
    dispatch(integrationActions.getAllActiveIntegrations(organizationId, isRefresh))
  }

  return (
    <React.Fragment>
      <div className={styles.integrationWrapper}>
        <div className={styles.activeIntegrations}>
          <div className={styles.activeIntegrationHeader}>
            <h3 className={styles.title}>{t('integration.activeIntegrations')}</h3>
            <TooltipWrapper
              position={isMobileScreen() ? 'top center' : 'right center'}
              triggerElement={
                <IconButton
                  iconName='update'
                  iconSize='large32'
                  onClick={() => getAllActiveIntegrations(orgId, true)}
                />
              }
              content={t('global.refresh')}
            />
          </div>
          <div className={styles.list}>
            {activeIntegrations.loading ? (
              <Spinner />
            ) : activeIntegrations.list.length === 0 ? (
              <NotFound title={t('integration.noIntegration')} />
            ) : null}

            {!activeIntegrations.loading && activeIntegrations.error && (
              <ErrorMessage text={activeIntegrations.error} />
            )}

            {!activeIntegrations.loading &&
              activeIntegrations?.list?.length > 0 &&
              activeIntegrations.list.map((conn) => {
                const { external_api, id_external_api_auth, api_user_id, is_default, usage } = conn
                let imageType = ''
                if (usage === 'virtual_meeting') {
                  imageType =
                    external_api === 'google'
                      ? 'google_meet_full'
                      : external_api === 'microsoft'
                      ? 'ms_teams_full'
                      : external_api === 'zoom'
                      ? 'zoom'
                      : ''
                } else if (usage === 'add_to_calendar') {
                  imageType =
                    external_api === 'google' || external_api === 'gcal'
                      ? 'google_cal_full'
                      : external_api === 'microsoft' || external_api === 'microsoftcal'
                      ? 'outlook_full'
                      : ''
                }

                return (
                  <IntegrationButton
                    from='user_settings'
                    imageType={imageType}
                    size='large'
                    isButton={false}
                    isDefault={is_default === 1}
                    email={api_user_id}
                    usage={usage}
                    authToken={id_external_api_auth}
                    integrationType={external_api}
                    orgId={orgId}
                    openConfirmModal={openConfirmModal}
                  />
                )
              })}
          </div>
        </div>
        <div className={styles.availableIntegrations}>
          <h3 className={styles.title}>{t('integration.availableIntegrations')}</h3>
          <div className={styles.videocalling}>
            <h4 className={styles.subTitle}>{t('integration.videoCalling')}</h4>
            <div className={styles.list}>
              {availableIntegrations?.list
                ?.filter((api) => api.usage === 'virtual_meeting' && api.is_active === 1)
                .map((api) => {
                  const imageType =
                    api.external_api === 'google'
                      ? `google_meet_${isMobileDevice ? 'small' : 'full'}`
                      : api.external_api === 'microsoft'
                      ? `ms_teams_${isMobileDevice ? 'small' : 'full'}`
                      : api.external_api === 'zoom'
                      ? 'zoom'
                      : ''

                  return (
                    <IntegrationButton
                      from='user_settings'
                      orgId={orgId}
                      imageType={imageType}
                      integrationType={api.external_api}
                      connectAsDefault={!isVirtualMeetDefaultExists(api.external_api)}
                      usage={api.usage}
                    />
                  )
                })}
            </div>
          </div>
          <div className={styles.calendars}>
            <h4 className={styles.subTitle}>{t('integration.calendars')}</h4>
            <div className={styles.list}>
              {availableIntegrations?.list
                ?.filter((api) => api.usage === 'add_to_calendar' && api.is_active === 1)
                .map((api) => {
                  const imageType =
                    api.external_api === 'google'
                      ? `google_cal_${isMobileDevice ? 'small' : 'full'}`
                      : api.external_api === 'microsoft'
                      ? `outlook_${isMobileDevice ? 'small' : 'full'}`
                      : ''
                  return (
                    <IntegrationButton
                      orgId={orgId}
                      imageType={imageType}
                      integrationType={api.external_api}
                      connectAsDefault={!isVirtualMeetDefaultExists(api.external_api)}
                      usage={api.usage}
                    />
                  )
                })}
            </div>
          </div>
        </div>

        <button type='button' className={styles.btnGDPR} onClick={openGDPRModal}>
          {t('gdpr.otherTitle')}
        </button>
        <GDPRCookieConsent isOpen={isGDPRModalOpen} screen='setting' closeGDPRModal={closeGDPRModal} />

        {isConfirmModalOpen && integrationToDelete && (
          <ConfirmModal
            loading={deleteIntegration.loading}
            isOpen={isConfirmModalOpen}
            title={t('integration.deleteConfirm', {
              integration: integrationToDelete.name,
              email: integrationToDelete.email
            })}
            primaryButtonText={t('global.confirm')}
            secondatyButtonText={t('global.cancel')}
            closeModal={closeConfirmModal}
            handlePrimaryButton={revokeIntegration}
            handleSecondaryButton={closeConfirmModal}
            hasTitleHTML={true}
          />
        )}
      </div>
    </React.Fragment>
  )
}

export default UserIntegrations
